/**
 * 表セクションの条件分岐(分岐質問)の更新。
 * templateNodes を入力に、条件分岐ノード配下の分岐質問の追加・削除を行う純粋関数。
 *
 *   列
 *   └─ 条件分岐("はい を選択した場合")
 *       ├─ 分岐質問0   ← ここに追加 / ここから削除
 *       └─ 分岐質問1
 *           └─ 条件分岐   ← 分岐質問も条件分岐を持てるので、親質問は列とは限らない
 */

import * as Sentry from '@sentry/browser'
import {
  ResponseTypeEnum,
  TemplateNodeSchema,
} from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { getCreateNodeId } from 'src/features/templateEdit/common/createNode'
import {
  appendBranchToCells,
  removeBranchFromCells,
  removeLogicFromCells,
} from './cellSync'
import type { ColumnInputType } from './columnType'
import {
  collectSubtreeNodeIds,
  getLogicBranchQuestions,
  getQuestionLogicNodes,
} from './selector'
import {
  buildNewQuestionNode,
  QUESTION_FROM_RESPONSE_TYPE,
} from './tableNodeBuilders'
import type { TableSectionUpdateWithActiveNode } from './tableSection'

/**
 * 条件分岐ノードに分岐質問を1つ追加する。
 * 追加した質問を activeNodeId(選択状態)として返し、設定パネルですぐ編集できるようにする。
 */
export const addTableBranchQuestion = (
  logicNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  responseType: ColumnInputType = ResponseTypeEnum.TEXT,
): TableSectionUpdateWithActiveNode => {
  const currentLogic = templateNodes[logicNode.id]
  if (!currentLogic) {
    const error = new Error(`条件分岐ノード ${logicNode.id} が見つかりません`)
    Sentry.captureException(error)
    throw error
  }

  const branchId = getCreateNodeId(templateNodes)
  const branchNode = buildNewQuestionNode(branchId, {
    ...QUESTION_FROM_RESPONSE_TYPE[responseType],
  })

  const previousBranchCount = getLogicBranchQuestions(
    currentLogic,
    templateNodes,
  ).length
  const nextNodes: TemplateNodesDict = {
    ...templateNodes,
    [branchId]: branchNode,
    [currentLogic.id]: {
      ...currentLogic,
      nodes: [...currentLogic.nodes, branchId],
    },
  }

  return {
    // 全固定行の対応する 条件分岐にも分岐質問を追加する(構造の同期)。
    templateNodes: appendBranchToCells(
      nextNodes,
      currentLogic.id,
      previousBranchCount,
    ),
    activeNodeId: branchId,
  }
}

/**
 * 分岐質問を1つ削除する。質問のサブツリーごと消し、親の条件分岐ノードの参照からも外す。
 * 削除後は条件分岐の親質問を選択状態にする(設定パネルの選択が浮かないように)。
 */
export const deleteTableBranchQuestion = (
  templateNodes: TemplateNodesDict,
  branchId: number,
  logicNode: TemplateNodeSchema,
  parentQuestionId: number,
): TableSectionUpdateWithActiveNode => {
  const currentLogic = templateNodes[logicNode.id]
  if (!currentLogic) {
    const error = new Error(`条件分岐ノード ${logicNode.id} が見つかりません`)
    Sentry.captureException(error)
    throw error
  }

  const branchIndex = getLogicBranchQuestions(
    currentLogic,
    templateNodes,
  ).findIndex(branch => branch.id === branchId)
  if (branchIndex < 0) {
    const error = new Error(
      `条件分岐ノード ${currentLogic.id} に分岐質問 ${branchId} がありません`,
    )
    Sentry.captureException(error)
    throw error
  }

  const idsToRemove = new Set(collectSubtreeNodeIds(branchId, templateNodes))
  const nextNodes: TemplateNodesDict = {}
  for (const [id, node] of Object.entries(templateNodes)) {
    if (!idsToRemove.has(Number(id))) {
      nextNodes[Number(id)] = node
    }
  }
  nextNodes[currentLogic.id] = {
    ...currentLogic,
    nodes: currentLogic.nodes.filter(id => id !== branchId),
  }

  return {
    // 全固定行の対応する 条件分岐からも同じ位置の分岐質問を削除する(構造の同期)。
    templateNodes: removeBranchFromCells(
      nextNodes,
      currentLogic.id,
      branchIndex,
    ),
    activeNodeId: parentQuestionId,
  }
}

/**
 * 条件分岐を分岐質問ごと削除する。親質問(列または分岐質問)の参照からも外す。
 * 削除後は親質問を選択状態にする。
 */
export const deleteTableLogic = (
  templateNodes: TemplateNodesDict,
  logicId: number,
  parentQuestionId: number,
): TableSectionUpdateWithActiveNode => {
  const parentQuestion = templateNodes[parentQuestionId]
  if (!parentQuestion) {
    const error = new Error(`親質問ノード ${parentQuestionId} が見つかりません`)
    Sentry.captureException(error)
    throw error
  }

  const logicIndex = getQuestionLogicNodes(
    parentQuestion,
    templateNodes,
  ).findIndex(logic => logic.id === logicId)
  if (logicIndex < 0) {
    const error = new Error(
      `質問ノード ${parentQuestionId} に条件分岐 ${logicId} がありません`,
    )
    Sentry.captureException(error)
    throw error
  }

  const idsToRemove = new Set(collectSubtreeNodeIds(logicId, templateNodes))
  const nextNodes: TemplateNodesDict = {}
  for (const [id, node] of Object.entries(templateNodes)) {
    if (!idsToRemove.has(Number(id))) {
      nextNodes[Number(id)] = node
    }
  }
  nextNodes[parentQuestion.id] = {
    ...parentQuestion,
    nodes: parentQuestion.nodes.filter(id => id !== logicId),
  }

  return {
    // 全固定行の対応する質問からも同じ位置の条件分岐をサブツリーごと削除する(構造の同期)。
    templateNodes: removeLogicFromCells(
      nextNodes,
      parentQuestionId,
      logicIndex,
    ),
    activeNodeId: parentQuestionId,
  }
}
